import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { TypedDocumentNode } from "@apollo/client";
import { useCompanyBuildings } from "@/hooks";
import { sortArrayByField } from "@/utils";
import type { BuildingMetersType, MongoCountriesType } from "@/types";
import { queries } from "@/graphql";

interface GetCountriesResponse {
  mongoCountries: Array<MongoCountriesType | null> | null;
}

const getCountriesTyped = queries.getCountries as TypedDocumentNode<
  GetCountriesResponse,
  Record<string, never>
>;

export const useCountries = (
  parent: string | null = null,
  child: string | null = null,
) => {
  const { data, loading } = useQuery(getCountriesTyped);
  const variables = useMemo(
    () => ({
      customerName: parent || "",
      subsidiaryName: child || "",
    }),
    [child, parent],
  );
  const { buildings } = useCompanyBuildings(variables);

  const countries = useMemo(() => {
    let returnValue: Array<MongoCountriesType> = [];

    if (data?.mongoCountries) {
      let filteredCountries: Array<MongoCountriesType> =
        data.mongoCountries.filter((country): country is MongoCountriesType =>
          Boolean(country),
        );
      if (parent && child) {
        const uniqueCountries = [
          ...new Set(
            buildings
              ?.map((building: BuildingMetersType) => building?.country)
              .filter((country): country is string => Boolean(country)) || [],
          ),
        ];
        filteredCountries = filteredCountries.filter(
          (country) =>
            Boolean(country.code) && uniqueCountries.includes(country.code),
        );
      }

      returnValue = sortArrayByField(
        filteredCountries,
        "name",
      ) as Array<MongoCountriesType>;
    }
    return returnValue;
  }, [buildings, child, data?.mongoCountries, parent]);

  return {
    countries,
    loading,
  };
};
